<template>
  <div class="thumb-admin">
    <h3>{{ $tt("缩略图补全任务") }}</h3>
    <el-card shadow="never" class="mb">
      <el-switch v-model="jobEnabled" :active-text="$tt('凌晨自动补全')" @change="onToggle" />
      <p class="switch-hint">
        {{ $tt("新上传图片已自动生成缩略图；此处仅补历史素材。点「立即执行」一次补全全部，每张写入任务明细。") }}
      </p>
      <el-button type="primary" size="small" :loading="running" @click="runNow">
        {{ $tt("立即执行（补全全部）") }}
      </el-button>
      <el-button size="small" @click="retryFailures">{{ $tt("重试失败项") }}</el-button>
      <el-button size="small" icon="el-icon-refresh" @click="refresh">{{ $tt("刷新") }}</el-button>
      <div class="stats" v-if="snapshot">
        <div class="progress-block">
          <div class="progress-head">
            <span class="progress-title">{{ $tt("图片缩略图补全进度") }}</span>
            <span class="progress-pct">{{ snapshot.progressPercent }}%</span>
          </div>
          <el-progress
            :percentage="snapshot.progressPercent"
            :status="snapshot.pendingTotal === 0 ? 'success' : undefined"
            :stroke-width="14"
          />
          <div class="progress-summary">
            {{ $tt("已完成") }}
            <strong>{{ snapshot.doneTotal }}</strong>
            {{ $tt("张") }} · {{ $tt("剩余") }}
            <strong>{{ snapshot.pendingTotal }}</strong>
            {{ $tt("张") }} · {{ $tt("合计") }}
            <strong>{{ snapshot.imageTotal }}</strong>
            {{ $tt("张图片素材") }}
          </div>
          <div class="progress-detail">
            <span>{{ $tt("私有") }}：{{ $tt("已完成") }} {{ snapshot.donePrivate }} / {{ $tt("待补") }} {{ snapshot.pendingPrivate }}</span>
            <span>{{ $tt("公共") }}：{{ $tt("已完成") }} {{ snapshot.donePublic }} / {{ $tt("待补") }} {{ snapshot.pendingPublic }}</span>
            <span>{{ $tt("失败待重试") }}：{{ snapshot.failureCount }}</span>
            <span v-if="snapshot.running" class="running-tag">{{ $tt("后台补全中…") }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <el-card shadow="never" class="mb">
      <div slot="header">{{ $tt("最近 3 次任务") }}</div>
      <el-table :data="runs" size="small" :max-height="listTableMaxHeight" @row-click="loadItems">
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="triggerType" :label="$tt('触发')" width="90" />
        <el-table-column prop="status" :label="$tt('状态')" width="90" />
        <el-table-column prop="totalPending" :label="$tt('开始时待补')" width="100" />
        <el-table-column prop="processed" :label="$tt('已处理')" width="80" />
        <el-table-column prop="successCount" :label="$tt('成功')" width="70" />
        <el-table-column prop="failCount" :label="$tt('失败')" width="70" />
        <el-table-column :label="$tt('开始')" min-width="140">
          <template slot-scope="scope">{{ formatTime(scope.row.startedAt) }}</template>
        </el-table-column>
        <el-table-column :label="$tt('结束')" min-width="140">
          <template slot-scope="scope">{{ formatTime(scope.row.finishedAt) }}</template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card shadow="never" class="mb" v-if="selectedRunId">
      <div slot="header">{{ $tt("周期明细") }} #{{ selectedRunId }}</div>
      <el-table :data="runItems" size="small" :max-height="Math.min(360, listTableMaxHeight)">
        <el-table-column prop="fileRealName" :label="$tt('名称')" min-width="120" show-overflow-tooltip />
        <el-table-column prop="status" :label="$tt('结果')" width="70" />
        <el-table-column :label="$tt('缩略图')" width="100">
          <template slot-scope="scope">
            <el-image
              v-if="scope.row.fileThumbPath"
              :src="scope.row.fileThumbPath"
              fit="contain"
              style="width: 64px; height: 48px"
              :preview-src-list="[scope.row.fileThumbPath]"
            />
          </template>
        </el-table-column>
        <el-table-column prop="errorMessage" :label="$tt('错误')" min-width="160" show-overflow-tooltip />
        <el-table-column prop="durationMs" :label="$tt('耗时ms')" width="80" />
      </el-table>
    </el-card>

    <el-card shadow="never">
      <div slot="header">{{ $tt("失败列表") }}</div>
      <el-table :data="failures" size="small" max-height="280">
        <el-table-column prop="sourceTable" :label="$tt('表')" width="140" />
        <el-table-column prop="fileId" :label="$tt('素材ID')" min-width="120" show-overflow-tooltip />
        <el-table-column prop="lastError" :label="$tt('错误')" min-width="200" show-overflow-tooltip />
        <el-table-column prop="retryCount" :label="$tt('重试次数')" width="90" />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import moment from "moment";

function pick(row, camel, snake) {
  const v = row[camel];
  if (v !== undefined && v !== null && v !== "") return v;
  return row[snake];
}

function normalizeRun(row) {
  if (!row) return row;
  return {
    id: row.id,
    triggerType: pick(row, "triggerType", "trigger_type"),
    status: row.status,
    processed: pick(row, "processed", "processed"),
    successCount: pick(row, "successCount", "success_count"),
    failCount: pick(row, "failCount", "fail_count"),
    totalPending: pick(row, "totalPending", "total_pending"),
    startedAt: pick(row, "startedAt", "started_at"),
    finishedAt: pick(row, "finishedAt", "finished_at"),
  };
}

function normalizeRunItem(row) {
  if (!row) return row;
  return {
    ...row,
    fileRealName: pick(row, "fileRealName", "file_real_name"),
    fileThumbPath: pick(row, "fileThumbPath", "file_thumb_path"),
    errorMessage: pick(row, "errorMessage", "error_message"),
    durationMs: pick(row, "durationMs", "duration_ms"),
  };
}

function normalizeFailure(row) {
  if (!row) return row;
  return {
    ...row,
    sourceTable: pick(row, "sourceTable", "source_table"),
    fileId: pick(row, "fileId", "file_id"),
    lastError: pick(row, "lastError", "last_error"),
    retryCount: pick(row, "retryCount", "retry_count"),
  };
}

function extractRunList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const d = payload.data != null ? payload.data : payload;
  if (Array.isArray(d)) return d;
  const nested = d.recentRuns || d.recent_runs || d.runs;
  return Array.isArray(nested) ? nested : [];
}

function extractFailureList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const d = payload.data != null ? payload.data : payload;
  if (Array.isArray(d)) return d;
  const nested = d.recentFailures || d.recent_failures || d.failures;
  return Array.isArray(nested) ? nested : [];
}

function num(snap, camel, snake) {
  const v = snap[camel] != null ? snap[camel] : snap[snake];
  if (v === undefined || v === null || v === "") return 0;
  return Number(v) || 0;
}

export default {
  listTableTopOffset: 380,
  data() {
    return {
      jobEnabled: true,
      running: false,
      snapshot: null,
      runs: [],
      runItems: [],
      selectedRunId: null,
      failures: [],
      pollTimer: null,
      autoRefreshTimer: null,
    };
  },
  mounted() {
    this.refresh();
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.autoRefreshTimer) clearInterval(this.autoRefreshTimer);
  },
  methods: {
    syncAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
      const s = this.snapshot;
      if (s && s.running) {
        this.autoRefreshTimer = setInterval(() => this.refresh(), 3000);
      }
    },
    formatTime(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      const m = Number.isFinite(n) && n > 1e11 ? moment(n) : moment(v);
      return m.isValid() ? m.format("YYYY-MM-DD HH:mm:ss") : String(v);
    },
    buildSnapshot(snap) {
      const pendingPrivate = num(snap, "pendingPrivate", "pending_private");
      const pendingPublic = num(snap, "pendingPublic", "pending_public");
      const donePrivate = num(snap, "donePrivate", "done_private");
      const donePublic = num(snap, "donePublic", "done_public");
      let pendingTotal = num(snap, "pendingTotal", "pending_total");
      let doneTotal = num(snap, "doneTotal", "done_total");
      let imageTotal = num(snap, "imageTotal", "image_total");
      if (!imageTotal && (pendingTotal || doneTotal)) {
        imageTotal = pendingTotal + doneTotal;
      }
      if (!pendingTotal) pendingTotal = pendingPrivate + pendingPublic;
      if (!doneTotal) doneTotal = donePrivate + donePublic;
      if (!imageTotal) imageTotal = pendingTotal + doneTotal;
      let progressPercent = num(snap, "progressPercent", "progress_percent");
      if (progressPercent == null && imageTotal > 0) {
        progressPercent = Math.round((doneTotal * 100) / imageTotal);
      }
      return {
        jobEnabled: snap.job_enabled != null ? snap.job_enabled : snap.jobEnabled,
        running: snap.running,
        pendingPrivate,
        pendingPublic,
        donePrivate,
        donePublic,
        pendingTotal,
        doneTotal,
        imageTotal,
        progressPercent: progressPercent != null ? progressPercent : 0,
        failureCount: num(snap, "failureCount", "failure_count") || 0,
      };
    },
    async refresh() {
      try {
        const st = await this.$request.thumbJobStatus();
        if (st.status === "success") {
          const snap = st.data || {};
          this.snapshot = this.buildSnapshot(snap);
          this.jobEnabled = !!this.snapshot.jobEnabled;
          this.running = !!this.snapshot.running;
          const fromStatus = extractRunList(snap);
          if (fromStatus.length || snap.recentRuns != null || snap.recent_runs != null) {
            this.runs = fromStatus.map(normalizeRun);
          }
          const failFromStatus = extractFailureList(snap);
          if (failFromStatus.length || snap.recentFailures != null || snap.recent_failures != null) {
            this.failures = failFromStatus.map(normalizeFailure);
          }
        } else if (st.msg) {
          this.$message.warning(st.msg);
        }
        if (!this.runs.length) {
          const rr = await this.$request.listThumbRuns(3);
          if (rr.status === "success") {
            this.runs = extractRunList(rr).map(normalizeRun);
          }
        }
        if (!this.failures.length) {
          const ff = await this.$request.failures();
          if (ff.status === "success") {
            this.failures = extractFailureList(ff).map(normalizeFailure);
          }
        }
      } catch (e) {
        console.error(e);
        this.$message.error(this.$tt("刷新失败，请查看网络或控制台"));
      }
      this.syncAutoRefresh();
    },
    async onToggle(val) {
      const res = await this.$request.setEnabled(val);
      if (res.status !== "success") this.$message.error(res.msg);
      else this.refresh();
    },
    async runNow() {
      this.running = true;
      try {
        const res = await this.$request.runNow();
        if (res.status !== "success") {
          this.$message.error(res.msg);
          this.running = false;
          return;
        }
        this.$message.success(this.$tt("已开始补全全部历史素材，请稍候刷新查看进度"));
        const data = res.data || {};
        const runId = data.run_id || data.runId;
        const runRow = data.run;
        if (runRow) {
          const normalized = normalizeRun(runRow);
          this.runs = [normalized, ...this.runs.filter((r) => r.id !== normalized.id)].slice(0, 3);
        }
        if (runId) {
          this.selectedRunId = runId;
          if (!runRow) {
            this.loadItems({ id: runId });
          }
        }
        await this.refresh();
        if (this.pollTimer) clearInterval(this.pollTimer);
        this.pollTimer = setInterval(async () => {
          await this.refresh();
          if (this.snapshot && !this.snapshot.running) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.running = false;
          }
        }, 2000);
      } catch (e) {
        console.error(e);
        this.$message.error(this.$tt("启动失败"));
        this.running = false;
      }
    },
    async retryFailures() {
      const res = await this.$request.retryFailures();
      if (res.status !== "success") this.$message.error(res.msg);
      else {
        this.$message.success(this.$tt("已触发"));
        this.refresh();
      }
    },
    async loadItems(row) {
      this.selectedRunId = row.id;
      const res = await this.$request.runItems(row.id);
      if (res.status === "success") {
        const list = Array.isArray(res.data) ? res.data : [];
        this.runItems = list.map(normalizeRunItem);
      }
    },
  },
};
</script>

<style scoped>
.thumb-admin {
  padding: 20px;
  background: #fff;
}
.mb {
  margin-bottom: 16px;
}
.stats {
  margin-top: 12px;
}
.progress-block {
  max-width: 720px;
}
.progress-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 14px;
  color: #303133;
}
.progress-pct {
  font-weight: 600;
  color: #409eff;
}
.progress-summary {
  margin-top: 10px;
  font-size: 14px;
  color: #303133;
}
.progress-summary strong {
  color: #409eff;
  font-size: 16px;
  margin: 0 2px;
}
.progress-detail {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.8;
}
.progress-detail span {
  display: inline-block;
  margin-right: 16px;
}
.running-tag {
  color: #e6a23c;
  font-weight: 600;
}
.switch-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.header-hint {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}
</style>
